// src/components/StudentSidebar.js
import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, User, BookOpen, LogIn, GraduationCap, Calendar, ListCheck, Info } from 'lucide-react';
import '../styles/Sidebar.css'; 

export default function Sidebar() {
    // rola użytkownika
    const [role, setRole] = useState('');
    // stan hamburgera (menu otwarte / zamknięte na mobile)
    const [menuOpen, setMenuOpen] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;

        async function fetchRole() {
            const cachedUser = localStorage.getItem('user');
            if (cachedUser) {
                try {
                    const user = JSON.parse(cachedUser);
                    if (user?.role) {
                        setRole(user.role);
                    }
                } catch (e) {}
            }

            const token = localStorage.getItem('token');
            if (!token && !cachedUser) {
                navigate('/');
                return;
            }

            const requestConfig = {
                params: { _: Date.now() },
                validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
                withCredentials: true,
            };

            if (token) {
                requestConfig.headers = { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                };
            }

            try {
                const response = await axios.get('/auth/me', requestConfig);
                if (!isMounted) return;
                
                if (response.status === 304) {
                    if (cachedUser) {
                        try {
                            const user = JSON.parse(cachedUser);
                            if (user?.role) {
                                setRole(user.role);
                                return;
                            }
                        } catch (e) {}
                    }
                    return;
                }
                
                const nextRole = response.data?.user?.role;
                if (nextRole) {
                    setRole(nextRole);
                    if (cachedUser) {
                        try {
                            const user = JSON.parse(cachedUser);
                            user.role = nextRole;
                            localStorage.setItem('user', JSON.stringify(user));
                        } catch (e) {}
                    }
                }
            } catch (error) {
                console.error('Nie udało się pobrać roli użytkownika', error);
                
                if (error.response?.status === 401 || error.response?.status === 403) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/');
                }
            }
        }

        fetchRole();

        return () => {
            isMounted = false;
        };
    }, [navigate]);

    // Mapowanie roli z tokena
    const getRoleLabel = (role) => {
      switch (role) {
        case 'administrator':
          return 'Administrator';
        case 'student':
          return 'Kursant';
        case 'lecturer':
          return 'Wykładowca';
        default:
          return '';
      }
    };

    const handleLogout = async () => {
        try {
            await axios.post('/auth/logout');
        } catch (error) {
            console.error('Błąd podczas wylogowywania', error);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/');
        }
    };

    // kliknięcie w link albo wyloguj na mobile – chowamy menu
    const closeMenu = () => setMenuOpen(false);

    return (
        <div className='sidebar'>
            {/* GÓRNY PAS – logo + tytuł + HAMBURGER */}
            <div className='sidebar-header'>
                <div className='sidebar-logo-icon'>
                    <img src="/images/logo.svg" alt="Logo szkoły" className='logo-image'/>
                </div>
                <div className='sidebar-header-title'>
                    <h3 className='header-title'>PSDiA</h3>
                    <p className="header-subtitle">{getRoleLabel(role)}</p>
                </div>

                {/* HAMBURGER – używa klas z Sidebar.css */}
                <button
                    className="sidebar-toggle"
                    type="button"
                    onClick={() => setMenuOpen((prev) => !prev)}
                    aria-label="Otwórz menu"
                >
                    <span className="sidebar-toggle-bar" />
                    <span className="sidebar-toggle-bar" />
                    <span className="sidebar-toggle-bar" />
                </button>
            </div>

            {/* MENU + WYLOGUJ – owijamy w .sidebar-main */}
            <div className={`sidebar-main ${menuOpen ? 'open' : ''}`}>
                <nav>
                    <NavLink
                        to={'/student/dashboard'}
                        className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
                        onClick={closeMenu}
                    >
                        <Home size={20} />
                        <span>Panel Główny</span>
                    </NavLink>

                    <NavLink
                        to={'/student/grades'}
                        className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
                        onClick={closeMenu}
                    >
                        <GraduationCap size={20} />
                        <span>Moje Oceny</span>
                    </NavLink>

                    <NavLink
                        to={'/student/schedule'}
                        className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
                        onClick={closeMenu}
                    >
                        <Calendar size={20} />
                        <span>Kalendarz zajęć</span>
                    </NavLink>

                    <NavLink
                        to={'/student/messages'}
                        className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
                        onClick={closeMenu}
                    >
                        <Info size={20} />
                        <span>Wiadomości</span>
                    </NavLink>

                    <NavLink
                        to={'/student/tasks'}
                        className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
                        onClick={closeMenu}
                    >
                        <ListCheck size={20} />
                        <span>Zadania</span>
                    </NavLink>

                    <NavLink
                        to={'/student/materials'}
                        className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
                        onClick={closeMenu}
                    >
                        <BookOpen size={20} />
                        <span>Materiały</span>
                    </NavLink>

                    <NavLink
                        to={'/student/profile'}
                        className={({ isActive }) => 'main-menu' + (isActive ? ' active' : '')}
                        onClick={closeMenu}
                    >
                        <User size={20} />
                        <span>Mój Profil</span>
                    </NavLink>
                </nav>

                <div className='logout-section'>
                    <div
                        className='nav-item-logout'
                        onClick={() => {
                            closeMenu();
                            handleLogout();
                        }}
                    >
                        <LogIn size={20} />
                        <span>Wyloguj</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
